import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useCompanyRegionExposure } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '@terminal/routes/query-params';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GeoExposureDetails,
  loadingSkeletonAreas,
} from '../components/geo-exposure-details/GeoExposureDetails';

export interface RegionExposureDrawerContentProps {
  entity: Entity;
}

export const RegionExposureDrawerContent = ({
  entity,
}: RegionExposureDrawerContentProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();
  const regionName = params[queryKeys.regionName];

  const { data, isFetching, isError, error } = useCompanyRegionExposure({
    entityTag: entity.tag,
  });

  const selected = useMemo(
    () => data?.find(item => item.name === regionName),
    [data, regionName]
  );

  if (isFetching) {
    return <SkeletonLoader areas={loadingSkeletonAreas} />;
  }

  if (isError || !selected || !regionName) {
    throw new Error(error?.message || 'Region data not found');
  }

  return (
    <GeoExposureDetails
      entity={entity}
      targetName={regionName}
      targetType="region"
      title={t(
        'widget:assetOverview.knowledgeGraph.geographicExposure.drawer.companyRegionExposure',
        {
          entity: entity.name_short || entity.name,
        }
      )}
      rankTitle={t(
        'widget:assetOverview.knowledgeGraph.geographicExposure.drawer.regionExposureRank'
      )}
      description={selected.relationship.description}
      rank={selected.relationship.rank}
      rankChange={selected.relationship.rank_change}
      evidence={selected.relationship.evidence}
      sourceEvidence={selected.relationship.source_evidence}
    />
  );
};
